// src/pages/reports/ProfitReport.jsx
import { useState, useEffect } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  AlertCircle,
  Package,
  Trash2,
  Download,
  RefreshCw,
  FileText,
  Printer
} from 'lucide-react'
import ProfitService from '../../services/profitService'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'
import { formatCurrency } from '../../utils/currency'
import { Button } from '../../components/ui/Button'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

const ProfitReport = () => {
  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id
  
  const [loading, setLoading] = useState(false)
  const [profitData, setProfitData] = useState(null)
  const [error, setError] = useState(null)
  const [branches, setBranches] = useState([])
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    branch: isManager ? (userBranchId || '') : ''
  })

  // Fetch branches for filter dropdown
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await BranchService.getAllBranches()
        if (response.success && response.data) {
          const branchList = response.data.branches || response.data
          // Include all active branches (outlets and warehouses)
          setBranches(branchList.filter(b => b.isActive))
        }
      } catch (err) {
        console.error('Error fetching branches:', err)
      }
    }
    fetchBranches()
  }, [])

  // Fetch profit data
  const fetchProfitData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('Fetching profit data with filters:', filters)
      const response = await ProfitService.getProfitCalculation(filters)
      console.log('Profit API Response:', response)
      
      if (response.success) {
        console.log('Setting profit data:', response.data)
        setProfitData(response.data)
      } else {
        console.error('API returned success=false:', response)
        setError(response.message || 'Failed to fetch profit data')
      }
    } catch (err) {
      console.error('Error fetching profit data:', err)
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        stack: err.stack
      })
      setError(err.message || 'An error occurred while fetching profit data')
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    fetchProfitData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleApplyFilters = () => {
    fetchProfitData()
  }

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      branch: isManager ? (userBranchId || '') : ''
    })
    // Fetch data with reset filters
    setTimeout(() => {
      fetchProfitData()
    }, 0)
  }

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (!profitData) {
        console.warn('No data to export')
        return
      }

      const wb = XLSX.utils.book_new()

      // Summary sheet
      const summaryData = [
        ['Profit Report'],
        ['Generated on', new Date().toLocaleString()],
        [''],
        ['Period', profitData.period?.from ? `${profitData.period.from} to ${profitData.period.to}` : 'N/A'],
        ['Branch', filters.branch ? branches.find(b => b._id === filters.branch)?.name : 'All Branches'],
        [''],
        ['Financial Summary'],
        ['Total Sales', profitData.summary?.totalSales || 0],
        ['Cost of Goods Sold', profitData.summary?.totalCOGS || 0],
        ['Deductions', profitData.summary?.totalDeductions || 0],
        ['Net Profit', profitData.summary?.netProfit || 0],
        ['Gross Margin', profitData.percentages?.grossMargin ? `${profitData.percentages.grossMargin}%` : '0%'],
        ['Profit Margin', profitData.percentages?.profitMargin ? `${profitData.percentages.profitMargin}%` : '0%'],
        [''],
        ['Sales Breakdown by Order Type'],
        ['Dine In', profitData.breakdown?.dineIn || 0],
        ['Delivery', profitData.breakdown?.delivery || 0],
        ['Takeaway', profitData.breakdown?.takeaway || 0],
      ]
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

      // Top Products sheet
      if (profitData.metrics?.topProducts && profitData.metrics.topProducts.length > 0) {
        const productsData = [
          ['Top Products by Revenue'],
          ['Product', 'Quantity Sold', 'Revenue', '% of Total Sales'],
          ...profitData.metrics.topProducts.map(p => [
            p.productName || 'N/A',
            p.quantity || 0,
            p.revenue || 0,
            p.percentOfSales ? `${p.percentOfSales}%` : '0%'
          ])
        ]
        const productsSheet = XLSX.utils.aoa_to_sheet(productsData)
        XLSX.utils.book_append_sheet(wb, productsSheet, 'Top Products')
      }

      const fileName = `Profit_Report_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(wb, fileName)
      console.log('✅ Excel export completed:', fileName)
    } catch (error) {
      console.error('❌ Error exporting to Excel:', error)
    }
  }

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (!profitData) {
        console.warn('No data to export')
        return
      }

      const doc = new jsPDF('portrait', 'pt', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()

      // Header
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      const title = 'Profit Report'
      const titleWidth = doc.getTextWidth(title)
      doc.text(title, (pageWidth - titleWidth) / 2, 40)

      // Date and filters
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      const dateText = `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`
      const dateWidth = doc.getTextWidth(dateText)
      doc.text(dateText, (pageWidth - dateWidth) / 2, 60)

      let yPos = 85

      // Period and Branch
      if (profitData.period?.from) {
        doc.text(`Period: ${profitData.period.from} to ${profitData.period.to}`, 40, yPos)
        yPos += 15
      }
      if (filters.branch) {
        const branchName = branches.find(b => b._id === filters.branch)?.name || 'Unknown'
        doc.text(`Branch: ${branchName}`, 40, yPos)
        yPos += 15
      }

      yPos += 10
      doc.setLineWidth(1)
      doc.line(40, yPos, pageWidth - 40, yPos)
      yPos += 25

      // Financial Summary
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Financial Summary', 40, yPos)
      yPos += 20

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Total Sales: Rs. ${(profitData.summary?.totalSales || 0).toFixed(2)}`, 40, yPos)
      yPos += 15
      doc.text(`Cost of Goods Sold: Rs. ${(profitData.summary?.totalCOGS || 0).toFixed(2)}`, 40, yPos)
      yPos += 15
      doc.text(`Deductions: Rs. ${(profitData.summary?.totalDeductions || 0).toFixed(2)}`, 40, yPos)
      yPos += 15
      doc.setFont('helvetica', 'bold')
      doc.text(`Net Profit: Rs. ${(profitData.summary?.netProfit || 0).toFixed(2)}`, 40, yPos)
      yPos += 20

      doc.setFont('helvetica', 'normal')
      doc.text(`Gross Margin: ${profitData.percentages?.grossMargin || 0}%`, 40, yPos)
      yPos += 15
      doc.text(`Profit Margin: ${profitData.percentages?.profitMargin || 0}%`, 40, yPos)
      yPos += 30

      // Sales Breakdown
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Sales Breakdown by Order Type', 40, yPos)
      yPos += 20

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`Dine In: Rs. ${(profitData.breakdown?.dineIn || 0).toFixed(2)}`, 40, yPos)
      yPos += 15
      doc.text(`Delivery: Rs. ${(profitData.breakdown?.delivery || 0).toFixed(2)}`, 40, yPos)
      yPos += 15
      doc.text(`Takeaway: Rs. ${(profitData.breakdown?.takeaway || 0).toFixed(2)}`, 40, yPos)
      yPos += 30

      // Top Products Table
      if (profitData.metrics?.topProducts && profitData.metrics.topProducts.length > 0) {
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.text('Top Products by Revenue', 40, yPos)
        yPos += 15

        const tableHeaders = ['Product', 'Quantity', 'Revenue', '% of Sales']
        const tableData = profitData.metrics.topProducts.map(p => [
          p.productName || 'N/A',
          (p.quantity || 0).toString(),
          `Rs. ${(p.revenue || 0).toFixed(2)}`,
          `${p.percentOfSales || 0}%`
        ])

        if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            head: [tableHeaders],
            body: tableData,
            startY: yPos,
            styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [249, 115, 22], textColor: [255, 255, 255], fontStyle: 'bold' },
            margin: { left: 40, right: 40 },
            theme: 'grid'
          })
        }
      }

      const fileName = `Profit_Report_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      console.log('✅ PDF export completed:', fileName)
    } catch (error) {
      console.error('❌ Error exporting to PDF:', error)
    }
  }

  // Print function
  const handlePrint = () => {
    try {
      if (!profitData) {
        console.warn('No data to print')
        return
      }

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Profit Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #000; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
            .header h1 { margin: 0; font-size: 24px; color: #333; }
            .header .date { margin: 5px 0 0 0; font-size: 12px; color: #666; }
            .section { margin-bottom: 25px; }
            .section h2 { font-size: 18px; color: #333; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            .metric-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
            .metric-label { font-weight: 600; }
            .metric-value { text-align: right; }
            .highlight { font-size: 16px; font-weight: bold; color: #f97316; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f97316; color: white; font-weight: bold; }
            tbody tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print { body { margin: 0; } .header { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Profit Report</h1>
            <p class="date">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            ${profitData.period?.from ? `<p class="date">Period: ${profitData.period.from} to ${profitData.period.to}</p>` : ''}
            ${filters.branch ? `<p class="date">Branch: ${branches.find(b => b._id === filters.branch)?.name || 'Unknown'}</p>` : ''}
          </div>
          
          <div class="section">
            <h2>Financial Summary</h2>
            <div class="metric-row">
              <span class="metric-label">Total Sales</span>
              <span class="metric-value">Rs. ${(profitData.summary?.totalSales || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Cost of Goods Sold</span>
              <span class="metric-value">Rs. ${(profitData.summary?.totalCOGS || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Deductions</span>
              <span class="metric-value">Rs. ${(profitData.summary?.totalDeductions || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row highlight">
              <span class="metric-label">Net Profit</span>
              <span class="metric-value">Rs. ${(profitData.summary?.netProfit || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Gross Margin</span>
              <span class="metric-value">${profitData.percentages?.grossMargin || 0}%</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Profit Margin</span>
              <span class="metric-value">${profitData.percentages?.profitMargin || 0}%</span>
            </div>
          </div>
          
          <div class="section">
            <h2>Sales Breakdown by Order Type</h2>
            <div class="metric-row">
              <span class="metric-label">Dine In</span>
              <span class="metric-value">Rs. ${(profitData.breakdown?.dineIn || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Delivery</span>
              <span class="metric-value">Rs. ${(profitData.breakdown?.delivery || 0).toFixed(2)}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Takeaway</span>
              <span class="metric-value">Rs. ${(profitData.breakdown?.takeaway || 0).toFixed(2)}</span>
            </div>
          </div>
          
          ${profitData.metrics?.topProducts && profitData.metrics.topProducts.length > 0 ? `
          <div class="section">
            <h2>Top Products by Revenue</h2>
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Revenue</th>
                  <th>% of Sales</th>
                </tr>
              </thead>
              <tbody>
                ${profitData.metrics.topProducts.map(p => `
                  <tr>
                    <td>${p.productName || 'N/A'}</td>
                    <td>${p.quantity || 0}</td>
                    <td>Rs. ${(p.revenue || 0).toFixed(2)}</td>
                    <td>${p.percentOfSales || 0}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Printed from Tezlaa Cafe Management System</p>
          </div>
        </body>
        </html>
      `

      const printWindow = window.open('', '_blank', 'width=800,height=600')
      printWindow.document.write(printContent)
      printWindow.document.close()

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print()
          printWindow.onafterprint = () => {
            printWindow.close()
          }
        }, 500)
      }

      console.log('✅ Print dialog opened')
    } catch (error) {
      console.error('❌ Error printing:', error)
    }
  }

  if (loading && !profitData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  const metrics = profitData?.metrics || {}
  const percentages = profitData?.percentages || {}
  const summary = profitData?.summary || {}
  const breakdown = profitData?.breakdown || {}
  
  const isProfit = metrics.profit >= 0

  return (
    <div className="space-y-6">
      {/* Header Section - Consistent with other reports */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Profit Report
            </h1>

          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchProfitData()}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={exportToPDF}
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>PDF</span>
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Excel</span>
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="flex items-center space-x-2"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-medium">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <select
                name="branch"
                value={filters.branch}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="primary"
              onClick={handleApplyFilters}
              className="flex-1"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Main Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Sales Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Sales</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(metrics.totalSales || 0)}</p>
          </div>
        </div>

        {/* COGS Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div>
            <p className="text-sm font-medium text-gray-600">Cost of Goods Sold</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(metrics.cogs || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">{(percentages.cogsPercentage || 0).toFixed(1)}% of sales</p>
          </div>
        </div>

        {/* Total Deductions Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Deductions</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(metrics.totalDeductions || 0)}</p>
            <div className="mt-1 space-y-0.5">
              <p className="text-xs text-gray-500">Waste: {formatCurrency(metrics.waste || 0)}</p>
              <p className="text-xs text-gray-500">Refunds: {formatCurrency(metrics.refunds || 0)}</p>
            </div>
          </div>
        </div>

        {/* Net Profit Card */}
        <div className={`rounded-lg shadow-sm border p-6 ${
          isProfit 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div>
            <p className={`text-sm font-medium ${isProfit ? 'text-green-700' : 'text-red-700'}`}>
              Net Profit
            </p>
            <p className={`text-2xl font-bold mt-2 ${isProfit ? 'text-green-900' : 'text-red-900'}`}>
              {formatCurrency(metrics.profit || 0)}
            </p>
            <p className={`text-xs mt-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {(summary.profitMargin || 0).toFixed(2)}% margin
            </p>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Gross Margin</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(summary.grossMargin || 0)}</p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Net Profit</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(summary.netProfit || 0)}</p>
          </div>
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Profit Margin</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{(summary.profitMargin || 0).toFixed(2)}%</p>
          </div>
        </div>
      </div>

      {/* Sales Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Breakdown by Order Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {breakdown.salesBreakdown && Object.entries(breakdown.salesBreakdown).map(([key, data]) => (
            <div key={key} className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-600 capitalize">
                {data.orderType?.replace('_', ' ') || key.replace('_', ' ')}
              </p>
              <p className="text-xl font-bold text-gray-900 mt-2">{formatCurrency(data.amount || 0)}</p>
              <p className="text-sm text-gray-500 mt-1">{(data.percentage || 0).toFixed(1)}% of total</p>
            </div>
          ))}
        </div>
      </div>

      {/* Top Products Table */}
      {breakdown.topProducts && breakdown.topProducts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Sales
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {breakdown.topProducts.map((product, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {product.product}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {product.quantity}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(product.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-right">
                      {((product.revenue / metrics.totalSales) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfitReport
